import { useEffect, useRef, useState } from 'react'

import { Box, Menu } from '@mantine/core'
import { RichTextEditor, Link } from '@mantine/tiptap'
import BulletList from '@tiptap/extension-bullet-list'
import Highlight from '@tiptap/extension-highlight'
import OrderedList from '@tiptap/extension-ordered-list'
import SubScript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import { useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import StyledMenu from 'src/components/Overlays/StyledMenu/StyledMenu'

interface VariableItem {
  name: string
  value: string // e.g. 'CompanyName' or 'cf:orientation_date'
}

interface BodyTextAreaProps {
  form: any
  onEditorReady?: (editor: any) => void
  enableVariableMentions?: boolean
  variableItems?: VariableItem[]
}

const BodyTextArea = ({
  form,
  onEditorReady,
  enableVariableMentions = false,
  variableItems = [],
}: BodyTextAreaProps) => {
  const [mentionOpen, setMentionOpen] = useState(false)
  const [selectedWord, setSelectedWord] = useState('')
  const skipOpenRef = useRef(false)

  const onChange = (value: string) => {
    form.setFieldValue('body', value)
  }

  const editor = useEditor({
    extensions: [
      StarterKit,
      BulletList.configure({
        HTMLAttributes: {
          class: 'list-disc',
        },
      }),
      OrderedList.configure({
        HTMLAttributes: {
          class: 'list-decimal',
        },
      }),
      Underline,
      Link,
      Superscript,
      SubScript,
      Highlight,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: form.values.body,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
      if (!enableVariableMentions) return
      if (skipOpenRef.current) {
        skipOpenRef.current = false
        setMentionOpen(false)
        return
      }
      const pos = editor.state.selection.from
      const before = editor.state.doc.textBetween(0, pos, ' ', ' ')
      const parts = before.split(/\s+/)
      const current = parts[parts.length - 1] ?? ''
      if (current.startsWith('@')) {
        setSelectedWord(current.slice(1))
        setMentionOpen(true)
      } else {
        setMentionOpen(false)
      }
    },
  })

  useEffect(() => {
    if (editor && form.values.body !== editor.getHTML()) {
      editor.commands.setContent(form.values.body || '', false)
    }
  }, [form.values.body, editor])

  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor)
    }
  }, [editor, onEditorReady])

  // Open/close on caret-only moves
  useEffect(() => {
    if (!editor || !enableVariableMentions) return
    const handler = () => {
      if (skipOpenRef.current) {
        skipOpenRef.current = false
        setMentionOpen(false)
        return
      }
      const pos = editor.state.selection.from
      const before = editor.state.doc.textBetween(0, pos, ' ', ' ')
      const parts = before.split(/\s+/)
      const current = parts[parts.length - 1] ?? ''
      if (current.startsWith('@')) {
        setSelectedWord(current.slice(1))
        setMentionOpen(true)
      } else {
        setMentionOpen(false)
      }
    }
    editor.on('selectionUpdate', handler)
    return () => {
      // @ts-expect-error tiptap types
      editor.off?.('selectionUpdate', handler)
    }
  }, [editor, enableVariableMentions])

  const filteredVariables = (variableItems ?? []).filter((v) =>
    v.name.toLowerCase().includes((selectedWord || '').toLowerCase())
  )

  const insertVariableAtCaret = (rawValue: string) => {
    if (!editor) return
    const pos = editor.state.selection.from
    const before = editor.state.doc.textBetween(0, pos, ' ', ' ')
    const parts = before.split(/\s+/)
    const current = parts[parts.length - 1] ?? ''
    const from = Math.max(0, pos - current.length)
    editor
      .chain()
      .focus()
      .deleteRange({ from, to: pos })
      .insertContent(`{{${rawValue}}}`)
      .run()
    skipOpenRef.current = true
    setMentionOpen(false)
  }

  return (
    <StyledMenu opened={enableVariableMentions && mentionOpen}>
      <Menu.Target>
        <RichTextEditor
          variant="subtle"
          editor={editor}
          className="flex h-full min-h-80 flex-col overflow-y-hidden border-0 shadow-none"
        >
          <Box
            className="max-h-96 flex-1 cursor-text overflow-y-auto"
            onClick={() => {
              editor?.commands.focus()
              editor?.commands.setTextSelection(editor.getHTML().length)
            }}
          >
            <RichTextEditor.Content
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
            />
          </Box>
          <RichTextEditor.Toolbar className="border-0 shadow-none">
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Bold />
              <RichTextEditor.Italic />
              <RichTextEditor.Underline />
              <RichTextEditor.Strikethrough />
              <RichTextEditor.ClearFormatting />
              <RichTextEditor.Highlight />
              <RichTextEditor.Code />
              <RichTextEditor.Link />
              <RichTextEditor.Unlink />
            </RichTextEditor.ControlsGroup>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Hr />
              <RichTextEditor.BulletList />
              <RichTextEditor.OrderedList />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.AlignLeft />
              <RichTextEditor.AlignCenter />
              <RichTextEditor.AlignJustify />
              <RichTextEditor.AlignRight />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Undo />
              <RichTextEditor.Redo />
            </RichTextEditor.ControlsGroup>
          </RichTextEditor.Toolbar>
        </RichTextEditor>
      </Menu.Target>
      <Menu.Dropdown
        className="max-h-[300px] overflow-y-auto"
        onFocus={() => editor?.commands.focus()}
      >
        {filteredVariables.map((v) => (
          <Menu.Item
            key={v.value}
            onClick={() => insertVariableAtCaret(v.value)}
            className="text-xs"
          >
            {v.name}
          </Menu.Item>
        ))}
        {filteredVariables.length === 0 && (
          <Menu.Item className="text-xs">No variables</Menu.Item>
        )}
      </Menu.Dropdown>
    </StyledMenu>
  )
}

export default BodyTextArea
